import { getActiveCloudServerKey } from '@/utils/cloudServer'
import { peekToken } from '@/api/auth'

const PC_PREVIEW_ENTRY_RESPONSE_TTL = 12 * 1000
const PC_PREVIEW_ENTRY_PENDING_TTL = 35 * 1000
const PC_PREVIEW_ENTRY_REQUEST_LIMIT = 16
const PC_PREVIEW_ENTRY_PREFETCH_PENDING_LIMIT = 2

const pcPreviewEntryRequests = new Map()

function getPcPreviewSessionKey() {
  const token = String(peekToken() || '')
  let hash = 0
  for (let index = 0; index < token.length; index += 1) {
    hash = ((hash * 31) + token.charCodeAt(index)) >>> 0
  }
  return `${token.length}-${hash.toString(16)}`
}

export default {
  beforeDestroy() {
    if (this.$options && this.$options.name == 'CloudHome') {
      pcPreviewEntryRequests.clear()
    }
  },
  methods: {
    isPcPreviewPrefetchGesture(event) {
      if (!event) {
        return true
      }
      if (event.ctrlKey || event.metaKey || event.shiftKey || event.altKey) {
        return false
      }
      if (event.button !== undefined && event.button !== 0) {
        return false
      }
      return Number(event.detail) <= 1
    },
    canPrefetchPcPreviewEntry() {
      if (typeof document !== 'undefined' && document.visibilityState == 'hidden') {
        return false
      }
      if (typeof navigator === 'undefined') {
        return true
      }
      if (navigator.onLine === false) {
        return false
      }
      const connection = navigator.connection || navigator.mozConnection || navigator.webkitConnection
      if (!connection) {
        return true
      }
      if (connection.saveData) {
        return false
      }
      if (connection.effectiveType == 'slow-2g' || connection.effectiveType == '2g') {
        return false
      }
      this.trimPcPreviewEntryRequests()
      const pendingPrefetchCount = Array.from(pcPreviewEntryRequests.values())
        .filter(entry => entry && entry.prefetched && !entry.settled)
        .length
      return pendingPrefetchCount < PC_PREVIEW_ENTRY_PREFETCH_PENDING_LIMIT
    },
    getPcPreviewCacheNamespace() {
      const serverKey = String(getActiveCloudServerKey() || 'default')
      return `${serverKey}:${getPcPreviewSessionKey()}`
    },
    getPcPreviewScopedCacheName(scope) {
      const normalizedScope = String(scope || '').trim()
      if (!normalizedScope) {
        return ''
      }
      return `${normalizedScope}:${this.getPcPreviewCacheNamespace()}`
    },
    getPcPreviewEntryRequestKey(scope, cacheKey) {
      const normalizedScope = String(scope || '').trim()
      const normalizedCacheKey = String(cacheKey || '').trim()
      if (!normalizedScope || !normalizedCacheKey) {
        return ''
      }
      return `${this.getPcPreviewCacheNamespace()}:${normalizedScope}:${normalizedCacheKey}`
    },
    getPcPreviewEntryRequest(scope, cacheKey) {
      const key = this.getPcPreviewEntryRequestKey(scope, cacheKey)
      if (!key) {
        return null
      }
      const entry = pcPreviewEntryRequests.get(key)
      if (!entry) {
        return null
      }
      const referenceAt = entry.settled ? entry.settledAt : entry.createdAt
      const ttl = entry.settled ? PC_PREVIEW_ENTRY_RESPONSE_TTL : PC_PREVIEW_ENTRY_PENDING_TTL
      if (Date.now() - referenceAt > ttl) {
        pcPreviewEntryRequests.delete(key)
        return null
      }
      entry.touchedAt = Date.now()
      pcPreviewEntryRequests.delete(key)
      pcPreviewEntryRequests.set(key, entry)
      return entry
    },
    trimPcPreviewEntryRequests(keepKey = '') {
      const now = Date.now()
      Array.from(pcPreviewEntryRequests.keys()).forEach(key => {
        const entry = pcPreviewEntryRequests.get(key)
        const referenceAt = entry && entry.settled ? entry.settledAt : (entry && entry.createdAt)
        const ttl = entry && entry.settled ? PC_PREVIEW_ENTRY_RESPONSE_TTL : PC_PREVIEW_ENTRY_PENDING_TTL
        if (key != keepKey && entry && now - referenceAt > ttl) {
          pcPreviewEntryRequests.delete(key)
        }
      })
      while (pcPreviewEntryRequests.size > PC_PREVIEW_ENTRY_REQUEST_LIMIT) {
        const removableKey = Array.from(pcPreviewEntryRequests.keys()).find(key => {
          const entry = pcPreviewEntryRequests.get(key)
          return key != keepKey && entry && entry.settled
        })
        if (!removableKey) {
          break
        }
        pcPreviewEntryRequests.delete(removableKey)
      }
    },
    isPcPreviewEntrySuccessfulResponse(response) {
      const data = response && response.data !== undefined ? response.data : response
      if (!data || typeof data != 'object') {
        return true
      }
      if (data.success === false) {
        return false
      }
      const code = data.code !== undefined ? data.code : data.statusCode
      if (code === undefined || code === null || code === '') {
        return true
      }
      return code == 200
    },
    primePcPreviewEntryRequest(scope, cacheKey, loader, options = {}) {
      const key = this.getPcPreviewEntryRequestKey(scope, cacheKey)
      if (!key || typeof loader != 'function') {
        return null
      }
      const existing = this.getPcPreviewEntryRequest(scope, cacheKey)
      if (existing) {
        return existing.promise
      }
      const entry = {
        createdAt: Date.now(),
        touchedAt: Date.now(),
        settled: false,
        settledAt: 0,
        prefetched: !!options.prefetch,
        promise: null
      }
      entry.promise = Promise.resolve()
        .then(() => loader())
        .then(
          response => options.prefetch && !this.isPcPreviewEntrySuccessfulResponse(response)
            ? { ok: false, retry: true }
            : { ok: true, response },
          error => ({ ok: false, error })
        )
      pcPreviewEntryRequests.set(key, entry)
      this.trimPcPreviewEntryRequests(key)
      entry.promise.then(result => {
        entry.touchedAt = Date.now()
        entry.settled = true
        entry.settledAt = entry.touchedAt
        if (!result.ok && pcPreviewEntryRequests.get(key) === entry) {
          pcPreviewEntryRequests.delete(key)
        }
      })
      return entry.promise
    },
    async resolvePcPreviewEntryRequest(scope, cacheKey, loader, options = {}) {
      const key = this.getPcPreviewEntryRequestKey(scope, cacheKey)
      if (!key || typeof loader != 'function') {
        return typeof loader == 'function' ? loader() : null
      }
      if (options.force) {
        pcPreviewEntryRequests.delete(key)
        return loader()
      }
      let entry = this.getPcPreviewEntryRequest(scope, cacheKey)
      if (!entry) {
        this.primePcPreviewEntryRequest(scope, cacheKey, loader)
        entry = this.getPcPreviewEntryRequest(scope, cacheKey)
      }
      if (!entry) {
        return loader()
      }
      const result = await entry.promise
      if (pcPreviewEntryRequests.get(key) === entry) {
        pcPreviewEntryRequests.delete(key)
      }
      if (result.ok) {
        return result.response
      }
      if (entry.prefetched) {
        return loader()
      }
      throw result.error
    },
    prefetchPcPaperDetail(item, event = null) {
      if (!item || !this.isPcPreviewPrefetchGesture(event) || !this.canPrefetchPcPreviewEntry()) {
        return null
      }
      if (typeof this.canOpenPaperResourceFolder == 'function' && this.canOpenPaperResourceFolder(item)) {
        return null
      }
      if (item.isFolder || !item.resourceId) {
        return null
      }
      if (typeof this.isPaperEncryptedItem == 'function' ? this.isPaperEncryptedItem(item) : item.encrypted) {
        return null
      }
      if (typeof this.isDirectFilePreviewItem == 'function' && this.isDirectFilePreviewItem(item)) {
        return null
      }
      if (typeof this.getPaperDetailCacheKey != 'function' || typeof this.fetchPaperDetailResponse != 'function') {
        return null
      }
      const cacheKey = this.getPaperDetailCacheKey(item, '')
      if (!cacheKey) {
        return null
      }
      if (typeof this.getCachedPaperDetail == 'function' && this.getCachedPaperDetail(cacheKey)) {
        return null
      }
      const scope = typeof this.getPaperDetailSharedCacheScope == 'function'
        ? this.getPaperDetailSharedCacheScope()
        : (this.$options && this.$options.name)
      return this.primePcPreviewEntryRequest(
        scope,
        cacheKey,
        () => this.fetchPaperDetailResponse(item, '', { skipAuthRefresh: true }),
        { prefetch: true }
      )
    },
    resolvePcPaperDetailResponse(item, password, loader, options = {}) {
      const cacheKey = typeof this.getPaperDetailCacheKey == 'function'
        ? this.getPaperDetailCacheKey(item, password || '')
        : ''
      const scope = typeof this.getPaperDetailSharedCacheScope == 'function'
        ? this.getPaperDetailSharedCacheScope()
        : (this.$options && this.$options.name)
      return this.resolvePcPreviewEntryRequest(scope, cacheKey, loader, options)
    }
  }
}
